import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import {
  ApplicationConfig,
  LOCALE_ID,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { ErrorStateMatcher } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TitleStrategy, provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { apiErrorInterceptor } from './shared/interceptors/api-error.interceptor';
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';
import { CustomIconRegistryService } from './shared/services/custom-icon-registry.service';
import { DeviceInfoService } from './shared/services/device-info.service';
import { EwentsTitleStrategy } from './shared/services/ewents-title-strategy';
import {
  installRevealedErrorState,
  OnDirtyOrSubmittedErrorMatcher,
} from './shared/utils/error-state-matchers';

import { environment } from '../environments/environment';
import {
  installMaterialTouchedErrors,
  provideEwentsToast,
  provideGoogleMapsApiKey,
} from '@ewentscompany/ewents-components';

installMaterialTouchedErrors();
installRevealedErrorState();

// `es` stays the app locale (numbers). `en` is registered so date pipes can force English.
registerLocaleData(localeEs);
registerLocaleData(localeEn);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimations(),
    // Outer → enrich after Auth logout handling; never auto-toast.
    provideHttpClient(withInterceptors([apiErrorInterceptor, AuthInterceptor])),
    provideGoogleMapsApiKey(environment.googleMapsApiKey ?? ''),
    ...provideEwentsToast(),
    { provide: LOCALE_ID, useValue: 'es' },
    // Always format the browser tab title as `${routeTitle} | Ewents CRM`,
    // and fall back to the app name when no route declares a title so the
    // previous page title never leaks into the next route.
    { provide: TitleStrategy, useClass: EwentsTitleStrategy },
    // Adopt the Figma UX: inputs only show the error state after the
    // control has been edited (dirty) or after a form submit, never just
    // because the user tabbed past an empty required field.
    { provide: ErrorStateMatcher, useClass: OnDirtyOrSubmittedErrorMatcher },
    provideAppInitializer(() => {
      inject(DeviceInfoService).initialize();
    }),
    provideAppInitializer(() => {
      inject(CustomIconRegistryService).initialize();
    }),
  ],
};
